import axios from '@Utils/httpService';
import store from '@/store';

const baseUrl = 'api/DashboardWidgets';

// The spatial-focus resolver is its OWN controller (MODS.Forge.Api SpatialFocusController.cs:24),
// not a DashboardWidgets route, so it must not be built from baseUrl.
const spatialFocusUrl = 'api/spatial-focus/resolve';

// ── Mock flag: set to false when backend endpoints are ready ──
const USE_MOCK = false;

/**
 * X-Facility-Id header when a facility is active (store.state.assetId = the current
 * facility). The data-source list (`/tables`) is facility-scoped server-side and fails
 * closed, so the header is omitted when no facility is active rather than sending an
 * empty value. Mirrors the facilityHeaders() helper used across the CML monitoring
 * services (e.g. cmlColoringService.js).
 * @returns {{ 'X-Facility-Id'?: string }}
 */
function facilityHeaders() {
  const assetId = store.state.assetId;
  return assetId ? { 'X-Facility-Id': assetId } : {};
}

function withFacility(config, facilityId) {
  const headers = { ...facilityHeaders(), ...config.headers };
  if (facilityId) headers['X-Facility-Id'] = facilityId;
  return { ...config, headers };
}

// ── Mock data generators for SQL View widget development ──
const mockHelpers = {
  delay(ms = 300) {
    return new Promise(resolve => setTimeout(resolve, ms));
  },

  mockResponse(data) {
    return this.delay().then(() => ({ data }));
  },

  // Available SQL views
  tables: [
    { name: 'vw_InspectionSummary', schema: 'dbo' },
    { name: 'vw_EquipmentStatus', schema: 'dbo' },
    { name: 'vw_CorrosionRates', schema: 'dbo' },
    { name: 'vw_ThicknessMeasurements', schema: 'dbo' },
    { name: 'vw_WorkOrders', schema: 'dbo' },
    { name: 'vw_RiskMatrix', schema: 'dbo' },
    { name: 'vw_PipingCircuits', schema: 'dbo' },
    { name: 'vw_CathodicProtection', schema: 'dbo' },
  ],

  // Column definitions per view
  columnsByTable: {
    vw_InspectionSummary: [
      { name: 'id', dataType: 'uniqueidentifier', nullable: false },
      { name: 'inspectionName', dataType: 'nvarchar', nullable: false },
      { name: 'tagNumber', dataType: 'nvarchar', nullable: false },
      { name: 'assetClass', dataType: 'nvarchar', nullable: true },
      { name: 'status', dataType: 'nvarchar', nullable: false },
      { name: 'inspectionType', dataType: 'nvarchar', nullable: true },
      { name: 'priority', dataType: 'nvarchar', nullable: true },
      { name: 'corrosionRate', dataType: 'decimal', nullable: true },
      { name: 'remainingThickness', dataType: 'decimal', nullable: true },
      { name: 'remainingLife', dataType: 'decimal', nullable: true },
      { name: 'scheduledDate', dataType: 'datetime', nullable: true },
      { name: 'completedDate', dataType: 'datetime', nullable: true },
      { name: 'facilityName', dataType: 'nvarchar', nullable: true },
      { name: 'unitName', dataType: 'nvarchar', nullable: true },
    ],
    vw_EquipmentStatus: [
      { name: 'id', dataType: 'uniqueidentifier', nullable: false },
      { name: 'tagNumber', dataType: 'nvarchar', nullable: false },
      { name: 'equipmentName', dataType: 'nvarchar', nullable: false },
      { name: 'equipmentType', dataType: 'nvarchar', nullable: true },
      { name: 'status', dataType: 'nvarchar', nullable: false },
      { name: 'operatingPressure', dataType: 'decimal', nullable: true },
      { name: 'operatingTemperature', dataType: 'decimal', nullable: true },
      { name: 'designPressure', dataType: 'decimal', nullable: true },
      { name: 'designTemperature', dataType: 'decimal', nullable: true },
      { name: 'installationDate', dataType: 'datetime', nullable: true },
      { name: 'lastInspectionDate', dataType: 'datetime', nullable: true },
      { name: 'nextInspectionDate', dataType: 'datetime', nullable: true },
      { name: 'facilityName', dataType: 'nvarchar', nullable: true },
    ],
    vw_CorrosionRates: [
      { name: 'id', dataType: 'uniqueidentifier', nullable: false },
      { name: 'tagNumber', dataType: 'nvarchar', nullable: false },
      { name: 'cmlName', dataType: 'nvarchar', nullable: true },
      { name: 'corrosionRate', dataType: 'decimal', nullable: false },
      { name: 'remainingLife', dataType: 'decimal', nullable: true },
      { name: 'nominalThickness', dataType: 'decimal', nullable: true },
      { name: 'measuredThickness', dataType: 'decimal', nullable: true },
      { name: 'tMin', dataType: 'decimal', nullable: true },
      { name: 'measurementDate', dataType: 'datetime', nullable: true },
      { name: 'corrosionType', dataType: 'nvarchar', nullable: true },
      { name: 'severity', dataType: 'nvarchar', nullable: true },
    ],
    vw_ThicknessMeasurements: [
      { name: 'id', dataType: 'uniqueidentifier', nullable: false },
      { name: 'tagNumber', dataType: 'nvarchar', nullable: false },
      { name: 'cmlId', dataType: 'nvarchar', nullable: true },
      { name: 'measuredThickness', dataType: 'decimal', nullable: false },
      { name: 'nominalThickness', dataType: 'decimal', nullable: true },
      { name: 'tMin', dataType: 'decimal', nullable: true },
      { name: 'measurementDate', dataType: 'datetime', nullable: false },
      { name: 'inspectorName', dataType: 'nvarchar', nullable: true },
      { name: 'method', dataType: 'nvarchar', nullable: true },
    ],
    vw_WorkOrders: [
      { name: 'id', dataType: 'uniqueidentifier', nullable: false },
      { name: 'woNumber', dataType: 'nvarchar', nullable: false },
      { name: 'tagNumber', dataType: 'nvarchar', nullable: true },
      { name: 'description', dataType: 'nvarchar', nullable: true },
      { name: 'status', dataType: 'nvarchar', nullable: false },
      { name: 'priority', dataType: 'nvarchar', nullable: true },
      { name: 'assignedTo', dataType: 'nvarchar', nullable: true },
      { name: 'createdDate', dataType: 'datetime', nullable: false },
      { name: 'dueDate', dataType: 'datetime', nullable: true },
      { name: 'completedDate', dataType: 'datetime', nullable: true },
      { name: 'estimatedHours', dataType: 'decimal', nullable: true },
      { name: 'actualHours', dataType: 'decimal', nullable: true },
    ],
    vw_RiskMatrix: [
      { name: 'id', dataType: 'uniqueidentifier', nullable: false },
      { name: 'tagNumber', dataType: 'nvarchar', nullable: false },
      { name: 'consequence', dataType: 'int', nullable: false },
      { name: 'likelihood', dataType: 'int', nullable: false },
      { name: 'riskLevel', dataType: 'nvarchar', nullable: false },
      { name: 'damageMechanism', dataType: 'nvarchar', nullable: true },
      { name: 'assessmentDate', dataType: 'datetime', nullable: true },
      { name: 'nextAssessmentDate', dataType: 'datetime', nullable: true },
    ],
    vw_PipingCircuits: [
      { name: 'id', dataType: 'uniqueidentifier', nullable: false },
      { name: 'circuitName', dataType: 'nvarchar', nullable: false },
      { name: 'tagNumber', dataType: 'nvarchar', nullable: false },
      { name: 'material', dataType: 'nvarchar', nullable: true },
      { name: 'nominalDiameter', dataType: 'decimal', nullable: true },
      { name: 'wallThickness', dataType: 'decimal', nullable: true },
      { name: 'operatingPressure', dataType: 'decimal', nullable: true },
      { name: 'fluidService', dataType: 'nvarchar', nullable: true },
      { name: 'corrosionAllowance', dataType: 'decimal', nullable: true },
    ],
    vw_CathodicProtection: [
      { name: 'id', dataType: 'uniqueidentifier', nullable: false },
      { name: 'tagNumber', dataType: 'nvarchar', nullable: false },
      { name: 'protectionType', dataType: 'nvarchar', nullable: true },
      { name: 'status', dataType: 'nvarchar', nullable: false },
      { name: 'potentialReading', dataType: 'decimal', nullable: true },
      { name: 'readingDate', dataType: 'datetime', nullable: true },
      { name: 'protectionStartDate', dataType: 'datetime', nullable: true },
      { name: 'nextSurveyDate', dataType: 'datetime', nullable: true },
    ],
  },

  // Generate mock rows for a given table
  generateRows(tableName, columns, count = 25) {
    const allColumns = this.columnsByTable[tableName];
    if (!allColumns) return [];

    const selectedCols = columns && columns.length
      ? allColumns.filter(c => columns.includes(c.name))
      : allColumns;

    const rows = [];
    for (let i = 0; i < count; i++) {
      const row = {};
      selectedCols.forEach(col => {
        row[col.name] = this.generateValue(col, i, tableName);
      });
      rows.push(row);
    }
    return rows;
  },

  generateValue(col, index, tableName) {
    const statuses = ['Active', 'Completed', 'Pending', 'Overdue', 'In Progress'];
    const priorities = ['Critical', 'High', 'Medium', 'Low'];
    const assetClasses = ['Vessel', 'Tank', 'Pipe', 'Heat Exchanger', 'Column', 'Pump'];
    const inspectionTypes = ['Visual', 'UT Thickness', 'Radiography', 'MFL', 'ACFM'];
    const severities = ['Low', 'Moderate', 'High', 'Severe'];
    const equipmentTypes = ['Pressure Vessel', 'Storage Tank', 'Heat Exchanger', 'Piping', 'Valve', 'Pump'];
    const materials = ['Carbon Steel', 'Stainless Steel 316L', 'Duplex SS', 'Inconel 625', 'Monel'];
    const fluids = ['Crude Oil', 'Natural Gas', 'Steam', 'Cooling Water', 'Amine', 'H2S'];
    const corrosionTypes = ['General', 'Pitting', 'Erosion', 'CUI', 'MIC', 'SCC'];
    const methods = ['UT', 'RT', 'Visual', 'ECT', 'PAUT'];
    const damageTypes = ['CO2 Corrosion', 'H2S Cracking', 'Fatigue', 'Erosion', 'Creep'];
    const protectionTypes = ['Sacrificial Anode', 'Impressed Current', 'Coating'];
    const cpStatuses = ['Protected', 'Under-Protected', 'Over-Protected', 'Not Monitored'];
    const names = ['Ahmad', 'Sara', 'Mohammed', 'Fatima', 'Omar', 'Layla'];
    const units = ['Unit-100', 'Unit-200', 'Unit-300', 'Unit-400', 'Unit-500'];
    const facilities = ['Plant Alpha', 'Plant Beta', 'Offshore Platform 1', 'Refinery South'];

    const pick = arr => arr[index % arr.length];
    const rand = (min, max, dec = 2) => +(min + Math.random() * (max - min)).toFixed(dec);
    const uuid = () => 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0;
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
    const randomDate = (startYear = 2022, endYear = 2026) => {
      const start = new Date(startYear, 0, 1).getTime();
      const end = new Date(endYear, 11, 31).getTime();
      return new Date(start + Math.random() * (end - start)).toISOString().split('T')[0];
    };

    switch (col.name) {
      case 'id': return uuid();
      case 'tagNumber': return `${pick(['V', 'T', 'P', 'E', 'C', 'PMP'])}-${100 + index}${pick(['A', 'B', ''])}`;
      case 'inspectionName': return `INS-${2024}-${String(index + 1).padStart(4, '0')}`;
      case 'assetClass': return pick(assetClasses);
      case 'status': return pick(statuses);
      case 'inspectionType': return pick(inspectionTypes);
      case 'priority': return pick(priorities);
      case 'corrosionRate': return rand(0.01, 2.5);
      case 'remainingThickness': return rand(3.0, 25.0);
      case 'remainingLife': return rand(0.5, 30.0);
      case 'scheduledDate': return randomDate(2024, 2027);
      case 'completedDate': return Math.random() > 0.4 ? randomDate(2023, 2026) : null;
      case 'facilityName': return pick(facilities);
      case 'unitName': return pick(units);
      case 'equipmentName': return `${pick(equipmentTypes)} ${pick(['Alpha', 'Beta', 'Gamma', 'Delta'])} ${index + 1}`;
      case 'equipmentType': return pick(equipmentTypes);
      case 'operatingPressure': return rand(1.0, 150.0);
      case 'operatingTemperature': return rand(20, 450, 0);
      case 'designPressure': return rand(50, 200.0);
      case 'designTemperature': return rand(100, 550, 0);
      case 'installationDate': return randomDate(2000, 2020);
      case 'lastInspectionDate': return randomDate(2022, 2025);
      case 'nextInspectionDate': return randomDate(2025, 2028);
      case 'cmlName': return `CML-${String(index + 1).padStart(3, '0')}`;
      case 'cmlId': return `CML-${String(index + 1).padStart(3, '0')}`;
      case 'nominalThickness': return rand(6.0, 30.0);
      case 'measuredThickness': return rand(3.0, 28.0);
      case 'tMin': return rand(2.0, 8.0);
      case 'measurementDate': return randomDate(2023, 2026);
      case 'corrosionType': return pick(corrosionTypes);
      case 'severity': return pick(severities);
      case 'inspectorName': return pick(names);
      case 'method': return pick(methods);
      case 'woNumber': return `WO-${2024}-${String(1000 + index).padStart(5, '0')}`;
      case 'description': return `${pick(['Inspect', 'Repair', 'Replace', 'Monitor', 'Survey'])} ${pick(assetClasses).toLowerCase()} ${pick(['coating', 'nozzle', 'shell', 'bottom plate', 'weld'])}`;
      case 'assignedTo': return pick(names);
      case 'createdDate': return randomDate(2024, 2026);
      case 'dueDate': return randomDate(2025, 2027);
      case 'estimatedHours': return rand(2, 40, 0);
      case 'actualHours': return Math.random() > 0.3 ? rand(1, 50, 0) : null;
      case 'consequence': return Math.floor(Math.random() * 5) + 1;
      case 'likelihood': return Math.floor(Math.random() * 5) + 1;
      case 'riskLevel': return pick(['Low', 'Medium', 'High', 'Very High', 'Extreme']);
      case 'damageMechanism': return pick(damageTypes);
      case 'assessmentDate': return randomDate(2023, 2026);
      case 'nextAssessmentDate': return randomDate(2026, 2029);
      case 'circuitName': return `PC-${String(index + 1).padStart(3, '0')}`;
      case 'material': return pick(materials);
      case 'nominalDiameter': return pick([2, 4, 6, 8, 10, 12, 16, 20, 24]);
      case 'wallThickness': return rand(3.0, 20.0);
      case 'fluidService': return pick(fluids);
      case 'corrosionAllowance': return rand(1.0, 6.0);
      case 'protectionType': return pick(protectionTypes);
      case 'potentialReading': return rand(-1200, -600, 0);
      case 'readingDate': return randomDate(2024, 2026);
      case 'protectionStartDate': return randomDate(2015, 2023);
      case 'nextSurveyDate': return randomDate(2025, 2028);
      default: return `Value_${index}`;
    }
  },

  // Apply mock aggregation to rows
  applyAggregation(rows, aggregation) {
    if (!aggregation || !aggregation.groupBy || !aggregation.groupBy.length) return null;

    const groups = {};
    rows.forEach(row => {
      const key = aggregation.groupBy.map(col => row[col] || 'null').join('||');
      if (!groups[key]) groups[key] = { _rows: [], _key: key };
      groups[key]._rows.push(row);
    });

    return Object.values(groups).map(group => {
      const result = {};
      aggregation.groupBy.forEach((col, i) => {
        result[col] = group._key.split('||')[i];
      });

      if (aggregation.metrics) {
        aggregation.metrics.forEach(metric => {
          const values = group._rows
            .map(r => parseFloat(r[metric.column]))
            .filter(v => !isNaN(v));
          const alias = metric.alias || `${metric.function}(${metric.column})`;

          switch (metric.function) {
            case 'COUNT': result[alias] = group._rows.length; break;
            case 'COUNT_DISTINCT': result[alias] = new Set(group._rows.map(r => r[metric.column]).filter(v => v != null)).size; break;
            case 'SUM': result[alias] = +(values.reduce((a, b) => a + b, 0)).toFixed(2); break;
            case 'AVG': result[alias] = values.length ? +(values.reduce((a, b) => a + b, 0) / values.length).toFixed(2) : 0; break;
            case 'MIN': result[alias] = values.length ? Math.min(...values) : 0; break;
            case 'MAX': result[alias] = values.length ? Math.max(...values) : 0; break;
            default: result[alias] = group._rows.length;
          }
        });
      }
      return result;
    });
  },

  // Apply mock filters to rows
  applyFilters(rows, filters) {
    if (!filters || !filters.length) return rows;

    return rows.filter(row => {
      return filters.every(f => {
        const val = row[f.column];
        const target = f.value;

        switch (f.operator) {
          case 'equals': return String(val) === String(target);
          case 'not_equals': return String(val) !== String(target);
          case 'contains': return String(val).toLowerCase().includes(String(target).toLowerCase());
          case 'gt': return parseFloat(val) > parseFloat(target);
          case 'lt': return parseFloat(val) < parseFloat(target);
          case 'gte': return parseFloat(val) >= parseFloat(target);
          case 'lte': return parseFloat(val) <= parseFloat(target);
          case 'is_null': return val === null || val === undefined;
          case 'is_not_null': return val !== null && val !== undefined;
          case 'between': {
            const [min, max] = Array.isArray(target) ? target : String(target).split(',');
            const n = parseFloat(val);
            return n >= parseFloat(min) && n <= parseFloat(max);
          }
          case 'in': {
            const list = Array.isArray(target) ? target : String(target).split(',');
            return list.map(s => String(s).trim()).includes(String(val));
          }
          default: return true;
        }
      });
    });
  },

  // Format aggregated data into chart-ready content
  formatForChart(aggregatedRows, aggregation, messageType) {
    if (!aggregatedRows || !aggregatedRows.length || !aggregation) return { content: aggregatedRows, series: [] };

    const labelCol = aggregation.groupBy[0];
    const metricAlias = aggregation.metrics && aggregation.metrics.length
      ? (aggregation.metrics[0].alias || `${aggregation.metrics[0].function}(${aggregation.metrics[0].column})`)
      : null;

    if (!metricAlias) return { content: aggregatedRows, series: [] };

    const content = aggregatedRows.map(row => ({
      label: String(row[labelCol]),
      value: row[metricAlias],
    }));

    return { content, series: [] };
  },

  // Extract distinct values for a column from mock data
  getDistinctValues(tableName, columnName, { search, take = 200 } = {}) {
    const rows = this.generateRows(tableName, null, 200);
    const seen = new Set();
    rows.forEach(row => {
      const val = row[columnName];
      if (val !== null && val !== undefined) seen.add(val);
    });
    let values = Array.from(seen).sort((a, b) => {
      if (typeof a === 'string') return a.localeCompare(b);
      return a - b;
    });
    if (search) {
      const s = String(search).toLowerCase();
      values = values.filter(v => String(v).toLowerCase().includes(s));
    }
    return values.slice(0, take);
  },

  // Apply top/bottom limiting to rows
  applyTopBottom(rows, topBottom) {
    if (!topBottom || !topBottom.mode || !topBottom.column || !topBottom.count) return rows;
    const sorted = [...rows].sort((a, b) => {
      const aVal = a[topBottom.column], bVal = b[topBottom.column];
      const cmp = aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      return topBottom.mode === 'top' ? -cmp : cmp;
    });
    return sorted.slice(0, topBottom.count);
  },
};

export default {
  create(widget) {
    return axios.post(withFacility({ url: baseUrl, data: widget }));
  },
  fromMessage(widget) {
    return axios.post(withFacility({ url: baseUrl+'/fromMessage', data: widget }));
  },
  generate(widget) {
    return axios.post(withFacility({ url: baseUrl + `/generate`, data: widget }));
  },
  // createManual(widget) {
  //   return axios.post(baseUrl+`/manual`,widget);
  // },
  update(widget) {
    return axios.put(withFacility({ url: baseUrl + `/${widget.id}`, data: widget }));
  },
  /**
   * Layout write used by the builder-v1 coalesced save queue. Same PUT as update()
   * but targets the CAPTURED facility (the one the edit session started on) via an
   * explicit X-Facility-Id header, so a flush that lands after the user has already
   * switched facility still writes to the dashboard they actually edited — never the
   * live, possibly already-switched facility. Falls back to the live facility header
   * when no captured facility is supplied.
   * @param {object} widget full widget object (carries captured pageId + x/y/width/height)
   * @param {{ facilityId?: string }} [context]
   */
  updateLayout(widget, context = {}) {
    return axios.put(withFacility(
      { url: baseUrl + `/${widget.id}`, data: widget },
      context.facilityId,
    ));
  },
  updateContent(widget) {
    return axios.put(withFacility({ url: baseUrl + `/${widget.id}/content`, data: widget }));
  },
  duplicate(id) {
    return axios.put(withFacility({ url: baseUrl + `/${id}/duplicate` }));
  },
  del(id) {
    return axios.delete(withFacility({ url: baseUrl + `/${id}` }));
  },
  // `signal` (optional AbortController.signal): lets the caller cancel a superseded widget query
  // on the wire when a newer filter replaces it. Passed to the axios config on BOTH paths; when
  // absent the httpService registry auto-manages cancellation as before (backward compatible).
  query(id, { filterSessionId, globalFilters, widgetFilters, signal } = {}) {
    const hasFilters = filterSessionId || (globalFilters && Object.keys(globalFilters).length) || widgetFilters;
    if (hasFilters) {
      const data = {};
      if (filterSessionId) data.filterSessionId = filterSessionId;
      if (globalFilters && Object.keys(globalFilters).length) data.globalFilters = globalFilters;
      if (widgetFilters) data.widgetFilters = widgetFilters;
      return axios.post(withFacility({ url: baseUrl + `/${id}/query`, data, signal }));
    }
    return axios.get(withFacility({ url: baseUrl + `/${id}`, signal }));
  },
  /**
   * Returns a plain-language summary of the persisted widget's calculation and current scope.
   */
  explain(id, payload = {}, { signal } = {}) {
    return axios.post(withFacility({ url: baseUrl + `/${id}/explain`, data: payload, signal }));
  },
  widgets(pageId) {
    return axios.get(withFacility({ url: baseUrl + `/${pageId}/widgets` }));
  },
  queries() {
    return axios.get(withFacility({ url: baseUrl + `/queries` }));
  },
  tables() {
    if (USE_MOCK) return mockHelpers.mockResponse(mockHelpers.tables);
    return axios.get(withFacility({ url: baseUrl + `/tables` }));
  },

  // ── SQL View Widget API methods ──────────────────────────────

  /** Returns column definitions for a given SQL view */
  tableColumns(tableName) {
    if (USE_MOCK) {
      const columns = mockHelpers.columnsByTable[tableName] || [];
      return mockHelpers.mockResponse(columns);
    }
    return axios.get(withFacility({ url: baseUrl + `/tables/${tableName}/columns` }));
  },

  /**
   * Returns distinct values for a column in a SQL view (widget filter checkboxes, KPI drill to list).
   * `widgetFilters` ({ filters: [{ column, operator, value }] }) narrows the values to a widget's
   * own scope; the server applies only its `filters` and ANDs them with the filter session, or with
   * `globalFilters` (the raw fallback) when no session is sent.
   */
  columnDistinctValues(tableName, columnName, { filterSessionId, globalFilters, search, take, widgetFilters } = {}) {
    if (USE_MOCK) {
      const values = mockHelpers.getDistinctValues(tableName, columnName, { search, take });
      return mockHelpers.mockResponse({ data: values, totalCount: values.length });
    }
    // The server scopes distinct values to the request's FacilityId and fails closed
    // (no rows) when it is absent, so send the current facility — matching the
    // body facilityId that preview/data/export already send. store.state.assetId is
    // the file's facility source (see facilityHeaders()).
    return axios.post(withFacility({
      url: baseUrl + `/tables/${tableName}/columns/${columnName}/values`,
      data: {
        facilityId: store.state.assetId,
        filterSessionId,
        search,
        take,
        ...(globalFilters ? { globalFilters } : {}),
        ...(widgetFilters ? { widgetFilters } : {}),
      },
    }));
  },

  /** Returns preview data (limited rows) for wizard preview panel */
  tablePreview(payload) {
    if (USE_MOCK) {
      const { tableName, columns, aggregation, filters } = payload;
      const take = payload.take || 25;
      let rows = mockHelpers.generateRows(tableName, columns, 100);
      rows = mockHelpers.applyFilters(rows, filters);

      if (aggregation && aggregation.groupBy && aggregation.groupBy.length) {
        const aggregatedRows = mockHelpers.applyAggregation(rows, aggregation);
        const formatted = mockHelpers.formatForChart(aggregatedRows, aggregation, payload.messageType);
        return mockHelpers.mockResponse({
          content: formatted.content,
          series: formatted.series,
          totalCount: formatted.content.length,
        });
      }

      return mockHelpers.mockResponse({
        content: rows.slice(0, take),
        series: [],
        totalCount: rows.length,
      });
    }
    return axios.post(withFacility({ url: baseUrl + '/tables/preview', data: payload }));
  },

  /** Returns paginated data for AG Grid infinite scroll */
  tableData(payload) {
    if (USE_MOCK) {
      const { tableName, columns, filters, skip = 0, take = 100, sortColumn, sortDirection } = payload;
      let rows = mockHelpers.generateRows(tableName, columns, 200);
      rows = mockHelpers.applyFilters(rows, filters);

      if (sortColumn) {
        rows.sort((a, b) => {
          const aVal = a[sortColumn], bVal = b[sortColumn];
          const cmp = aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
          return sortDirection === 'desc' ? -cmp : cmp;
        });
      }

      return mockHelpers.mockResponse({
        rows: rows.slice(skip, skip + take),
        totalCount: rows.length,
      });
    }
    return axios.post(withFacility({ url: baseUrl + '/tables/data', data: payload }));
  },

  /** Returns CSV/Excel blob for export */
  tableExport(payload) {
    if (USE_MOCK) {
      const { tableName, columns, filters } = payload;
      let rows = mockHelpers.generateRows(tableName, columns, 200);
      rows = mockHelpers.applyFilters(rows, filters);

      const headers = Object.keys(rows[0] || {});
      const csvLines = [headers.join(',')];
      rows.forEach(row => {
        csvLines.push(headers.map(h => `"${row[h] !== null && row[h] !== undefined ? row[h] : ''}"`).join(','));
      });

      const blob = new Blob([csvLines.join('\n')], { type: 'text/csv' });
      return mockHelpers.mockResponse(blob);
    }
    return axios.post(withFacility({ url: baseUrl + '/tables/export', data: payload, responseType: 'blob' }));
  },

  /** Maps a tag name to Forge viewer dbIds for 3D isolation */
  resolveTagDbIds(tag, facilityId) {
    if (USE_MOCK) {
      // Simulate finding dbIds for a tag — returns 1-3 mock dbIds
      const hash = tag.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
      return mockHelpers.mockResponse({
        dbIds: [hash % 5000 + 1, hash % 5000 + 2, hash % 5000 + 3],
        tagNumber: tag,
      });
    }
    return axios.get(withFacility(
      { url: baseUrl + `/resolveTag?tag=${encodeURIComponent(tag)}&facilityId=${facilityId}` },
      facilityId,
    ));
  },

  /** Resolves tag values matching a filter condition (for chart legend cross-filtering) */
  resolveTagsByFilter(payload) {
    return axios.post(withFacility({ url: baseUrl + '/tables/resolveTagsByFilter', data: payload }));
  },

  /**
   * Resolves the linked tags of the rows matching a datagrid's CURRENT filter. Send the same
   * filter fields the grid datasource sends to /tables/data (tableName, columns, filters,
   * facilityId, filterSessionId|globalFilters, search) plus an optional coloringColumn. Backs the
   * grid "Isolate All" action. Responds with { tags: [{ id, dbIds, coloringValue }], total, cap }:
   * tags is capped at `cap` distinct tags (ordered by tag id); `total` is the full pre-cap
   * distinct-tag count so the caller can report "first N of M" when the set was trimmed.
   */
  resolveFilteredTags(payload) {
    return axios.post(withFacility({ url: baseUrl + '/tables/resolveFilteredTags', data: payload }));
  },

  /** Creates a server-side filter session — returns a sessionId for efficient widget queries */
  createFilterSession(payload) {
    return axios.post(withFacility({ url: baseUrl + '/filterSession', data: payload }));
  },

  /**
   * Resolves one exact spatial focus — the single server call behind `useSpatialFocus`.
   *
   * The payload is passed through UNTOUCHED on purpose. `SpatialFocusRequestValidator` refuses any
   * field it does not own: a body `facilityId`/`assetId` is `facilityNotBodyAuthority`, a
   * `globalFilters` is `globalFiltersNotSupported`, a `take`/`limit`/`maxOccurrences` is
   * `callerBudgetNotSupported`, and anything else is `unknownField`. So this method adds no model
   * list, no global filter and no budget — the facility travels in the existing X-Facility-Id
   * header (see facilityHeaders()) and the budgets are server constants echoed back in
   * `response.limits`. Note that `columnDistinctValues` above deliberately DOES send a body
   * facilityId; that route wants it, this one 400s on it.
   *
   * `signal` (AbortController.signal) cancels a superseded focus on the wire, matching query()
   * above: a chart click that lands while an earlier one is still resolving must not race.
   *
   * @param {{ scope: object }} payload exactly one scope arm — query | rows | entities
   * @param {{ signal?: AbortSignal }} [options]
   */
  resolveSpatialFocus(payload, { signal } = {}) {
    return axios.post(withFacility({ url: spatialFocusUrl, data: payload, signal }));
  },
};
